import React, { useEffect, useState } from 'react';
import {
	makeStyles,
	CircularProgress,
	Backdrop,
	Box
} from '@material-ui/core';
import { Personal, Contact, Other, Addresses, Documents, Notes, Header } from './components';
import { Redirect } from 'react-router-dom';
import { RoundedAddButton } from '../../../components/Common';
import { AddNote } from './components';
import compareDesc from 'date-fns/compareDesc';
import { CustomerModel, CustomerTagModel } from '@/models/customer';
import { apiClient } from '@/api/client';
import { useDocumentTitle } from '@/hooks/use-document-title';

const useStyles = makeStyles(theme => ({
	contentWrapper: {
		'& .MuiTextField-root': {
			marginRight: theme.spacing(10),
			marginTop: theme.spacing(5),
		},
		'& h1': {
			fontSize: '25px',
		},
		[theme.breakpoints.up('md')]: {
			paddingLeft: 50,
			paddingRight: 50,
		},
		[theme.breakpoints.up('lg')]: {
			paddingLeft: 65,
			paddingRight: 65,
		},
		[theme.breakpoints.up('xl')]: {
			paddingLeft: 88,
			paddingRight: 88,
		},
		[theme.breakpoints.down('sm')]: {
			paddingTop: 20,
			paddingBottom: 20,
		},
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#00000',
		background: 'none',
		opacity: '0.7',
	},
	customerName: {
		float: 'left',
	},
	customerLTV: {
		float: 'right',
	},
}));

export const CustomerDetail = (props: any) => {
	const classes = useStyles();
	const formEnabled = false;
	const [customer, setCustomer] = useState<CustomerModel | null>();
	const [isReady, setIsReady] = useState<boolean>(false);
	const [tabIndex, setTabIndex] = useState<number>(0);
	const [showAddNote, setShowAddNote] = useState<boolean>(false);

	useDocumentTitle('Customer Details');

	useEffect(() => {
		async function fetchCustomer () {
			try {
				const result = await apiClient.customer.getCustomer(props.match.params.id);

				setCustomer(result);
			} catch {
				setCustomer(null);
			} finally {
				setIsReady(true);
			}
		}

		fetchCustomer();
	}, []);

	if (customer === null) {
		return <Redirect to='/404' />;
	}

	const TabPanel = ({ children, value, index, ...other }: any) => {
		return (
			<Box
				role="tabpanel"
				hidden={value !== index}
				{...other}>
				{value === index && (
					<Box p={3}>
						{children}
					</Box>
				)}
			</Box>
		);
	};

	const addCustomerTag = (customerTag: CustomerTagModel) => {
		if (!customer || !customerTag.customerNote) {
			return;
		}

		setCustomer({
			...customer,
			customerNotes: [
				...customer.customerNotes!,
				customerTag.customerNote
			],
			customerTags: [
				...customer.customerTags!,
				customerTag,
			]
		});
	};

	return (
		<>
			{customer &&
				<Header customer={customer} tabIndex={tabIndex} onTabIndexChanged={setTabIndex} />
			}
			<div className={classes.contentWrapper}>
				{isReady === false
					&& <Backdrop className={classes.backdrop} open={!isReady}>
						<CircularProgress color="inherit" />
					</Backdrop>}

				{customer &&
					<>
						<AddNote
							customerId={customer.id}
							show={showAddNote}
							onSuccess={customerTag => addCustomerTag(customerTag)}
							onClose={() => setShowAddNote(false)} />
						<RoundedAddButton onClick={() => setShowAddNote(true)} />

						<TabPanel value={tabIndex} index={0}>
							<div data-id="tabPersonalInfo">
								<Personal customer={customer} formEnabled={formEnabled} />
								<Contact customer={customer} formEnabled={formEnabled} />
								<Other customer={customer} formEnabled={formEnabled} />
								<Addresses customerAddresses={customer.addresses} primaryAddressId={customer.primaryAddressId} />
								<Documents customerDocuments={customer.documents} />
							</div>
						</TabPanel>

						<TabPanel value={tabIndex} index={1}>
							<div data-id="tabAllNotes">
								<Notes
									customerNotes={customer.customerNotes?.sort((noteA, noteB) => compareDesc(new Date(noteA.createdAt), new Date(noteB.createdAt)))}
								/>
							</div>
						</TabPanel>
					</>
				}
			</div>
		</>
	);
};
